import React, { type FC, type ReactElement, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'

import type { PaginationOptions } from '../../../../domain'

import { RecordPanel, type TableProps } from '../../common'
import { type AbsoluteRoutePathWithAnyId, DetachedRoute } from '../../routing'
import { useAppUsecase } from '../../State'
import { RecordPanelContentProvider } from './Content'

type HostedRecord = Readonly<{ id: string }>

type ShownPagination = PaginationOptions & Readonly<{ totalCount: number }>

type Props<R extends HostedRecord> = Readonly<{
    /** Where a row navigates to, which Expand does */
    route: Readonly<{ path: AbsoluteRoutePathWithAnyId, redirect: (id: string) => void }>
    details: FC
    /** The rows shown by the table */
    rows: readonly R[]
    /** If the page requested is the one shown */
    loaded: boolean
    /** If the page requested failed to load */
    failed: boolean
    /** The page of the `rows`, a new object each time a page loads */
    pagination: ShownPagination | null
    turnPage: (page: number) => void
}>

type StepDirection = 'previous' | 'next'

/** A record of the shown page to open, or a page to turn to */
type Step = Readonly<{ id: string }> | Readonly<{ page: number }>

/** Zero-based `index` */
type Position = Readonly<{ index: number, total: number }>

type Location = Readonly<{ position: Position, previous: Step | null, next: Step | null }>

/** Where the record is in the whole list, if it's among the shown rows */
const locate = (rows: readonly HostedRecord[], id: string, pagination: ShownPagination | null): Location | null => {
    const index = rows.findIndex((row) => row.id === id)

    if (index < 0 || pagination === null) {
        return null
    }

    const { page, pageSize, totalCount } = pagination
    const position = { index: page * pageSize + index, total: totalCount }
    const previous = rows[index - 1]
    const next = rows[index + 1]

    return {
        position,
        previous: previous ? { id: previous.id } : position.index > 0 ? { page: page - 1 } : null,
        next: next ? { id: next.id } : position.index + 1 < totalCount ? { page: page + 1 } : null,
    }
}

/** A step across a page boundary, waiting for the page to load */
type PendingStep = Readonly<{
    direction: StepDirection
    page: number
    /** The page shown when turning */
    from: ShownPagination | null
    /** If the focus was on the open row, to move it to the record opened */
    focusRow: boolean
}>

/** The record to open once the pending step's page has loaded, `null` if there is none, `undefined` while waiting */
const resolvePendingStep = <R extends HostedRecord>(
    { direction, page, from }: PendingStep,
    { rows, loaded, failed, pagination }: Pick<Props<R>, 'rows' | 'loaded' | 'failed' | 'pagination'>
): R | null | undefined => {
    if (failed) {
        return null
    }

    /** Still the page turned from, also in a render before the request has reached the state */
    if (!loaded || pagination === from) {
        return undefined
    }

    if (pagination?.page !== page) {
        return null
    }

    return (direction === 'next' ? rows[0] : rows[rows.length - 1]) ?? null
}

type RecordPanelHost<R extends HostedRecord> = Readonly<{
    tableProps: Required<Pick<TableProps<R>, 'onRowClick' | 'isRowActive' | 'rowRef'>>
    /** The record whose own actions the panel's modals have taken over, while they are mounted */
    modalsOwnerId: string | null
    onOpenRecordSelected: (selected: boolean) => void
    /** Nothing while no record is open */
    panel: ReactElement | null
}>

type OpenRecordPanelProps =
    & Pick<Props<HostedRecord>, 'route' | 'details'>
    & Readonly<{
        id: string
        position: Position | null
        onPrevious: VoidFunction | undefined
        onNext: VoidFunction | undefined
        open: boolean
        title: string | null
        /** Changes each time the panel opens, so reopening it while it leaves shows its record afresh */
        opening: number
        openRecordSelected: boolean
        /** The rendered rows, to give the focus back to */
        rowElements: ReadonlyMap<string, HTMLTableRowElement>
        hide: VoidFunction
        onExited: VoidFunction
        onTitle: (title: string) => void
        onModalsOwner: (id: string | null) => void
        width: number | null
        onWidthChange: (width: number | null) => void
    }>

const OpenRecordPanel: FC<OpenRecordPanelProps> = ({
    route,
    details: Details,
    id,
    position,
    onPrevious,
    onNext,
    open,
    title,
    opening,
    openRecordSelected,
    rowElements,
    hide,
    onExited,
    onTitle,
    onModalsOwner,
    width,
    onWidthChange,
}) => {
    const content = useMemo(
        () => ({
            close: () => {
                hide()

                const row = rowElements.get(id)
                if (row?.isConnected) {
                    row.focus()
                }
            },
            dismiss: hide,
            openRecordSelected,
            showTitle: onTitle,
            takeOverModals: onModalsOwner,
        }),
        [hide, rowElements, id, openRecordSelected, onTitle, onModalsOwner]
    )

    /**
     * The same element across the panel's own changes, such as its title or position, so they don't re-render the details
     *
     * TODO: CC-5041 (https://cybusio.atlassian.net/browse/CC-5041) - read the open record here, so the details don't report it up and this memo goes
     */
    const details = useMemo(
        () => (
            <DetachedRoute key={opening} path={route.path} id={id}>
                <RecordPanelContentProvider value={content}>
                    <Details />
                </RecordPanelContentProvider>
            </DetachedRoute>
        ),
        [opening, route.path, id, content, Details]
    )

    return (
        <RecordPanel
            open={open}
            title={title ?? id}
            position={position}
            onPrevious={onPrevious}
            onNext={onNext}
            onExpand={() => route.redirect(id)}
            onClose={content.close}
            onExited={onExited}
            width={width}
            onWidthChange={onWidthChange}
        >
            {details}
        </RecordPanel>
    )
}

/**
 * A table's rows open in a record panel, one at a time, rather than navigating
 *
 * - focus stays on the row; closing gives it back to the open row, if still rendered
 * - the position counts across the whole list, hidden when the open row isn't shown
 * - ↑ ↓ step through the rows, turning the page at its ends: the next page opens on its first row, the previous one on its last.
 *   Stepping is unavailable while a page loads; a turned page that fails or has no rows leaves the panel on its record
 * - Expand navigates where the row would have
 * - the panel's width is the one saved, read again each time the panel opens
 */
export const useRecordPanelHost = <R extends HostedRecord>({ route, details, rows, loaded, failed, pagination, turnPage }: Props<R>): RecordPanelHost<R> => {
    const recordPanelWidthUsecase = useAppUsecase('recordPanelWidthUsecase')
    /** The record shown, kept while the panel leaves */
    const [openId, setOpenId] = useState<string | null>(null)
    const [open, setOpen] = useState(false)
    const [title, setTitle] = useState<string | null>(null)
    const [modalsOwnerId, setModalsOwnerId] = useState<string | null>(null)
    const [openRecordSelected, setOpenRecordSelected] = useState(false)
    const [opening, setOpening] = useState(0)
    const [pendingStep, setPendingStep] = useState<PendingStep | null>(null)
    const [width, setWidth] = useState<number | null>(null)
    const rowElements = useRef(new Map<string, HTMLTableRowElement>())
    /** The record whose row takes the focus once rendered, as the table renders a turned page's rows after it has loaded */
    const focusOnRender = useRef<string | null>(null)

    const show = useCallback(
        (id: string) => {
            if (id !== openId) {
                setTitle(null)
            }

            if (!open) {
                setOpening((count) => count + 1)
                setWidth(recordPanelWidthUsecase.get())
            }

            setOpenId(id)
            setOpen(true)
            setPendingStep(null)
            focusOnRender.current = null
        },
        [openId, open, recordPanelWidthUsecase]
    )

    const onWidthChange = useCallback(
        (changed: number | null) => {
            setWidth(changed)
            recordPanelWidthUsecase.save(changed)
        },
        [recordPanelWidthUsecase]
    )

    const hide = useCallback(() => {
        setOpen(false)
        setPendingStep(null)
        focusOnRender.current = null
    }, [])

    const onExited = useCallback(() => {
        setOpenId(null)
        setTitle(null)
    }, [])

    const rowRef = useCallback((row: R, element: HTMLTableRowElement | null) => {
        if (element) {
            rowElements.current.set(row.id, element)

            if (focusOnRender.current === row.id) {
                focusOnRender.current = null
                element.focus()
            }
        } else {
            rowElements.current.delete(row.id)
        }
    }, [])

    /** Before paint, so the panel doesn't show its record without a position in between */
    useLayoutEffect(() => {
        if (pendingStep === null) {
            return
        }

        const target = resolvePendingStep(pendingStep, { rows, loaded, failed, pagination })

        if (target === undefined) {
            return
        }

        setPendingStep(null)

        if (target === null) {
            return
        }

        show(target.id)

        if (pendingStep.focusRow) {
            focusOnRender.current = target.id
        }
    }, [pendingStep, rows, loaded, failed, pagination, show])

    const location = openId === null ? null : locate(rows, openId, pagination)

    const toStepHandler = (step: Step | null | undefined, direction: StepDirection): VoidFunction | undefined => {
        if (!step || !loaded || pendingStep !== null || openId === null) {
            return undefined
        }

        if ('id' in step) {
            return () => show(step.id)
        }

        return () => {
            setPendingStep({ direction, page: step.page, from: pagination, focusRow: document.activeElement === rowElements.current.get(openId) })
            turnPage(step.page)
        }
    }

    return {
        tableProps: { onRowClick: ({ id }) => show(id), isRowActive: ({ id }) => open && id === openId, rowRef },
        modalsOwnerId,
        onOpenRecordSelected: setOpenRecordSelected,
        panel: openId === null ? null : (
            <OpenRecordPanel
                route={route}
                details={details}
                id={openId}
                position={location?.position ?? null}
                onPrevious={toStepHandler(location?.previous, 'previous')}
                onNext={toStepHandler(location?.next, 'next')}
                open={open}
                title={title}
                opening={opening}
                openRecordSelected={openRecordSelected}
                rowElements={rowElements.current}
                hide={hide}
                onExited={onExited}
                onTitle={setTitle}
                onModalsOwner={setModalsOwnerId}
                width={width}
                onWidthChange={onWidthChange}
            />
        ),
    }
}
